/**
 * Steam Top-up flow — Django parity for `service_id=1`.
 *
 * Two reads + one buy:
 *   1. /steam_topup/service  → service_id + markup multiplier
 *   2. /exchange_rate {1}    → rub/kzt/uah multipliers for the side table
 *   3. create_order(amount, login) + pay_order under the hood
 *
 * The amount input is the user-facing USD value. The BUY button shows
 * `amount × markup` so the user knows the final charge before clicking.
 * Server still re-calculates authoritatively at /create_order time.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { api, ApiError } from "./client";

// ---- service resolver ----------------------------------------------------

export interface SteamTopupService {
  service_id: number;
  service_name: string | null;
  /**
   * Effective per-USD multiplier — already adjusted for the caller's
   * partner status / terms_new. Multiply amount by this to get the
   * USD total to show next to BUY.
   */
  price: number;
  is_partner: boolean;
  /**
   * Server-resolved regex for the Steam-login (`account`) field, from
   * the same schema /create_order enforces. The SPA validates against
   * this so the rule lives only in the DB. Null → use the built-in
   * fallback pattern.
   */
  login_validation_rule: string | null;
}

export function useSteamTopupService() {
  return useQuery<SteamTopupService, ApiError>({
    queryKey: ["steam_topup", "service"],
    queryFn: () => api.get<SteamTopupService>("/web/api/steam_topup/service"),
    staleTime: 5 * 60_000,
  });
}

// ---- side table currency rates ------------------------------------------

export interface ExchangeRateResponse {
  service_id: number;
  date: string;
  rates: Record<string, number>;
}

export function useSteamCurrencyRates() {
  return useQuery<ExchangeRateResponse, ApiError>({
    queryKey: ["exchange_rate", 1],
    queryFn: () =>
      api.post<ExchangeRateResponse>("/web/api/exchange_rate", {
        body: { service_id: 1 },
      }),
    staleTime: 60_000,
  });
}

// ---- local-currency → USD conversion (web-only convenience) -------------
//
// Lets the SPA accept the amount the customer wants credited in their
// local currency (RUB/KZT/UAH) and convert it to the USD value the
// (unchanged) create_order flow expects. Backed by the existing
// /web/api/steam/get_amount endpoint, which uses the canonical
// steam_currency rate and rounds UP to cents — so it's authoritative,
// not client-side guesswork. The /api/v2 flow is untouched (USD-only).

export interface SteamGetAmountResponse {
  region: string;
  /** Regional units per 1 USD. */
  exchange_rate: number;
  /** USD value of the regional amount, rounded UP to cents. */
  usd_price: number;
}

export function useSteamGetAmount(params: {
  amount: number;
  region: string; // "ru" | "kz" | "ua"
  enabled?: boolean;
}) {
  return useQuery<SteamGetAmountResponse, ApiError>({
    queryKey: ["steam_topup", "get_amount", params.region, params.amount],
    queryFn: () =>
      api.post<SteamGetAmountResponse>("/web/api/steam/get_amount", {
        body: { amount: params.amount, region: params.region },
      }),
    enabled: (params.enabled ?? true) && params.amount > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ---- buy ----------------------------------------------------------------

export interface SteamTopupBuyParams {
  service_id: number;
  amount: number;
  login: string;
  totp_code?: string;
}

export interface SteamTopupBuyResult {
  custom_id: string;
  status: "completed" | "refunded" | "insufficient" | "in_progress";
  balance: string;
}

export function useBuySteamTopup() {
  return useMutation<SteamTopupBuyResult, ApiError, SteamTopupBuyParams>({
    mutationFn: async (params) => {
      const customId = crypto.randomUUID();
      await api.post<{ custom_id: string }>("/web/api/orders/create", {
        body: {
          service_id: params.service_id,
          custom_id: customId,
          fields: [
            { key: "account", value: params.login },
            { key: "amount", value: params.amount },
          ],
        },
      });
      const pay = await api.post<{
        custom_id: string;
        status: SteamTopupBuyResult["status"];
        balance: string;
      }>("/web/api/orders/pay", {
        body: {
          custom_id: customId,
          ...(params.totp_code ? { totp_code: params.totp_code } : {}),
        },
      });
      return {
        custom_id: customId,
        status: pay.status,
        balance: pay.balance,
      };
    },
  });
}
